export const ALLOWED_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/heic',
  'image/heif'
];

export const MAX_TOTAL_PIXELS = 50000000; // 50,000,000px
export const MAX_SIDE = 2000;

let heicToModule = null;
let heicCache = new WeakMap();

export const clearHeicCache = () => {
  heicCache = new WeakMap();
};

export const isHeicFile = (file) => {
  const isHeicMime = file.type === 'image/heic' || file.type === 'image/heif';
  const isHeicExt = /\.(heic|heif)$/i.test(file.name);
  return isHeicMime || isHeicExt;
};

export const isSupportedFile = (file, allowedMimeTypes = ALLOWED_MIME_TYPES) => {
  const isAllowedMime = allowedMimeTypes.includes(file.type);
  const isAllowedExt = /\.(jpe?g|png|heic|heif|webp|gif)$/i.test(file.name);
  return isAllowedMime || isAllowedExt;
};

const convertHeicIfNeeded = async (file) => {
  if (!isHeicFile(file)) {
    return file;
  }

  if (heicCache.has(file)) {
    return heicCache.get(file);
  }

  if (!heicToModule) {
    const mod = await import('heic-to');
    heicToModule = mod.heicTo;
  }

  const blob = await heicToModule({
    blob: file,
    type: 'image/jpeg',
    quality: 0.8
  });

  const convertedFile = new File(
    [blob],
    file.name.replace(/\.(heic|heif)$/i, '.jpeg'),
    {
      type: 'image/jpeg',
      lastModified: Date.now()
    }
  );

  heicCache.set(file, convertedFile);
  return convertedFile;
};

export const processImage = async (file, t, options = {}) => {
  let currentFile = file;

  const {
    maxTotalPixels = MAX_TOTAL_PIXELS,
    maxSide = MAX_SIDE
  } = options;

  try {
    currentFile = await convertHeicIfNeeded(file);
  } catch (error) {
    if (String(error).includes('HEIF image not found')) {
      throw new Error(t('common.114009'));
    }
    throw new Error(t('error.10313'));
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(currentFile);
    img.src = objectUrl;

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      if (img.width * img.height > maxTotalPixels) {
        return reject(new Error(t('common.114009')));
      }

      let width = img.width;
      let height = img.height;

      if (width > maxSide || height > maxSide) {
        if (width > height) {
          height = Math.round(height * (maxSide / width));
          width = maxSide;
        } else {
          width = Math.round(width * (maxSide / height));
          height = maxSide;
        }
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error(t('error.10313')));
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob((blob) => {
        if (!blob) return reject(new Error(t('error.10313')));
        const newFileName = currentFile.name ? currentFile.name.replace(/\.[^/.]+$/, '') + '.jpeg' : 'image.jpeg';
        resolve(new File([blob], newFileName, { type: 'image/jpeg', lastModified: Date.now() }));
      }, 'image/jpeg', 0.8);
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error(t('common.114009')));
    };
  });
};